import { PlayerState } from "amazon-ivs-player";
import type { MediaPlayer } from "amazon-ivs-player";

export type ReconnectResult = "loaded" | "retry" | "stop";
type Playback = Pick<MediaPlayer, "getPosition" | "getState" | "isPaused">;

// Reload only when playback stops making progress. Never seek a healthy stream.
export function createPlaybackRecovery(
  player: Playback,
  reconnect: (isCurrent: () => boolean) => Promise<ReconnectResult>,
  isVisible = () => !document.hidden,
) {
  let disposed = false;
  let stopped = false;
  let pending = false;
  let generation = 0;
  let failures = 0;
  let interrupted = false;
  let lastPosition: number | undefined;
  let nextAttemptAt = Date.now() + 15000;

  const playing = () => {
    generation += 1; // Ignore responses started before playback recovered.
    interrupted = false;
    failures = 0;
    lastPosition = player.getPosition();
    nextAttemptAt = Date.now() + 8000;
  };

  const buffering = () => {
    interrupted = true;
    // Repeated BUFFERING events must not postpone recovery forever.
    nextAttemptAt = Math.min(nextAttemptAt, Date.now() + 8000);
  };

  const failed = () => {
    interrupted = true;
    nextAttemptAt = Math.min(nextAttemptAt, Date.now() + 3000);
  };

  const attempt = async () => {
    if (pending) return;
    pending = true;
    const currentGeneration = generation;
    const isCurrent = () => !disposed && !stopped && isVisible()
      && navigator.onLine !== false && generation === currentGeneration;
    nextAttemptAt = Infinity;
    try {
      const result = await reconnect(isCurrent);
      if (!isCurrent()) return;
      if (result === "stop") {
        stopped = true;
        return;
      }
      failures = result === "loaded" ? 0 : failures + 1;
      const delay = result === "loaded" ? 8000 : Math.min(3000 * failures, 10000);
      nextAttemptAt = Math.min(nextAttemptAt, Date.now() + delay);
    } catch {
      if (isCurrent()) {
        failures += 1;
        nextAttemptAt = Date.now() + Math.min(3000 * failures, 10000);
      }
    } finally {
      pending = false;
    }
  };

  const check = () => {
    if (disposed || stopped || !isVisible() || navigator.onLine === false) return;
    const state = player.getState();
    const position = player.getPosition();
    if (state === PlayerState.PLAYING && !player.isPaused() && Number.isFinite(position)) {
      if (lastPosition !== undefined && position > lastPosition + 0.05) {
        playing();
      } else if (lastPosition === undefined) {
        lastPosition = position;
      }
    }
    // A user pause or blocked autoplay should keep its play button.
    if (!interrupted && player.isPaused() && (state === PlayerState.PLAYING || state === PlayerState.READY)) return;
    if (Date.now() >= nextAttemptAt) void attempt();
  };

  const onVisibilityChange = () => {
    if (!isVisible()) {
      generation += 1;
      nextAttemptAt = Math.min(nextAttemptAt, Date.now() + 8000);
    } else {
      if (interrupted) nextAttemptAt = Date.now();
      check();
    }
  };
  const onOnline = () => {
    nextAttemptAt = Date.now();
    check();
  };
  const interval = window.setInterval(check, 1000);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("online", onOnline);

  return {
    playing,
    buffering,
    failed,
    dispose: () => {
      disposed = true;
      generation += 1;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
    },
  };
}
